import { useSearchParams } from 'react-router-dom';
import { cn } from '@/lib/cn';
import { BehaviorBankPanel } from './panels/BehaviorBankPanel';
import { ProgramBankPanel } from './panels/ProgramBankPanel';
import { PlansPanel } from './panels/PlansPanel';
import { TemplatesPanel } from './panels/TemplatesPanel';

type TabKey = 'plans' | 'templates' | 'behaviors' | 'programs';

const TABS: Array<{ key: TabKey; label: string }> = [
    { key: 'plans',     label: 'Treatment Plans' },
    { key: 'templates', label: 'Templates' },
    { key: 'behaviors', label: 'Behavior Bank' },
    { key: 'programs',  label: 'Program Bank' },
];

const DEFAULT_TAB: TabKey = 'plans';

function parseTab(raw: string | null): TabKey {
    return TABS.some((t) => t.key === raw) ? (raw as TabKey) : DEFAULT_TAB;
}

export function TreatmentPlansPage() {
    const [searchParams, setSearchParams] = useSearchParams();
    const active = parseTab(searchParams.get('tab'));

    function selectTab(key: TabKey) {
        const next = new URLSearchParams(searchParams);
        if (key === DEFAULT_TAB) next.delete('tab');
        else next.set('tab', key);
        setSearchParams(next, { replace: false });
    }

    return (
        <div className="p-6 max-w-[1200px] mx-auto">
            <div className="mb-1">
                <h1 className="font-display text-2xl font-bold text-text">Treatment Plans</h1>
            </div>
            <p className="text-xs text-text-subtle mb-4">
                Client treatment plans, reusable plan templates, and the behavior + program
                banks that feed them.
            </p>

            <div className="flex gap-1 border-b border-border mb-5" role="tablist" aria-label="Treatment plan sections">
                {TABS.map((t) => (
                    <button
                        key={t.key}
                        type="button"
                        role="tab"
                        aria-selected={active === t.key}
                        onClick={() => selectTab(t.key)}
                        className={cn(
                            'px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors',
                            active === t.key
                                ? 'text-text border-accent'
                                : 'text-text-muted border-transparent hover:text-text',
                        )}
                    >
                        {t.label}
                    </button>
                ))}
            </div>

            {active === 'plans' && <PlansPanel />}
            {active === 'templates' && <TemplatesPanel />}
            {active === 'behaviors' && <BehaviorBankPanel />}
            {active === 'programs' && <ProgramBankPanel />}
        </div>
    );
}
